import React from 'react';
import { PHASE_LABELS, type BreadcrumbPhase } from '../lib/breadcrumbsCatalog';
import type { FunnelResult, FunnelRow } from '../lib/breadcrumbFunnelService';

function heatPct(row: FunnelRow): number {
  if (row.band === 'pre' || row.band === 'stair') return Math.max(0, Math.min(100, row.barPct));
  return Math.max(0, Math.min(100, row.pctOfCohort));
}

function heatStyle(pct: number): { background: string; color: string } {
  const t = Math.max(0, Math.min(100, pct)) / 100;
  const stops: [number, number, number][] = [
    [255, 237, 213],
    [253, 186, 116],
    [251, 146, 60],
    [234, 88, 12],
    [154, 52, 18],
  ];
  const x = t * (stops.length - 1);
  const i = Math.min(Math.floor(x), stops.length - 2);
  const f = x - i;
  const a = stops[i];
  const b = stops[i + 1];
  const r = Math.round(a[0] + (b[0] - a[0]) * f);
  const g = Math.round(a[1] + (b[1] - a[1]) * f);
  const bl = Math.round(a[2] + (b[2] - a[2]) * f);
  return {
    background: `rgb(${r}, ${g}, ${bl})`,
    color: t > 0.52 ? '#fff7ed' : '#7c2d12',
  };
}

function groupByPhase(rows: FunnelRow[]): { phase: string; rows: FunnelRow[] }[] {
  const groups: { phase: string; rows: FunnelRow[] }[] = [];
  for (const row of rows) {
    const last = groups[groups.length - 1];
    if (!last || last.phase !== row.phase) groups.push({ phase: row.phase, rows: [row] });
    else last.rows.push(row);
  }
  return groups;
}

function HeatTile({ row }: { row: FunnelRow }) {
  const pct = heatPct(row);
  const style = heatStyle(pct);
  const tooltip = [
    row.label,
    `${row.reached} personne${row.reached === 1 ? '' : 's'}`,
    row.band === 'pre' || row.band === 'stair'
      ? `${row.barPct} % du haut de l’escalier`
      : `${row.pctOfCohort} % des inscrits`,
    row.dropOff != null && row.dropOff > 0 ? `perte ${row.dropOff} % vers la suivante` : null,
    row.medianHoursToNext != null ? `délai médian ${row.medianHoursToNext} h` : null,
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <div
      className={`rounded-lg p-2.5 min-h-[5.5rem] flex flex-col justify-between border ${
        row.fork ? 'border-amber-400 ring-1 ring-amber-300' : 'border-black/5'
      }`}
      style={style}
      title={tooltip}
    >
      <div className="flex items-start justify-between gap-1">
        <span className="text-[10px] font-mono opacity-70">{row.n}</span>
        {row.fork && (
          <span className="text-[8px] font-bold uppercase tracking-wide px-1 rounded bg-amber-200 text-amber-800">
            split
          </span>
        )}
      </div>
      <p className="text-[12px] font-medium leading-tight line-clamp-2 mt-1">{row.label}</p>
      <div className="mt-2 flex items-end justify-between gap-1">
        <span className="text-lg font-bold leading-none tabular-nums">{Math.round(pct)}%</span>
        <span className="text-[10px] opacity-80 tabular-nums">{row.reached}</span>
      </div>
    </div>
  );
}

export function BreadcrumbHeatmap({ data }: { data: FunnelResult }) {
  const groups = groupByPhase(data.rows);
  const legend = [0, 25, 50, 75, 100];

  return (
    <div className="p-6 space-y-5">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-sm text-gray-600">
            Cohorte : <span className="font-semibold text-gray-900">{data.cohort}</span>
            {data.role === 'candidate' ? ' candidats' : ' recruteurs'} inscrits sur la période
          </p>
          <p className="text-xs text-gray-500 mt-1 max-w-2xl">
            Plus la case est chaude, plus de monde est encore là. Sur l’activation et les wizards,
            la couleur suit l’escalier (ceux qui ont aussi fait les marches au-dessus). Ailleurs,
            c’est la part des inscrits qui ont fait l’action.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[10px] uppercase tracking-wide text-gray-400 font-semibold">Peu</span>
          <div className="flex h-3 w-36 rounded-full overflow-hidden border border-gray-200">
            {legend.map((p) => (
              <div key={p} className="flex-1" style={{ background: heatStyle(p).background }} />
            ))}
          </div>
          <span className="text-[10px] uppercase tracking-wide text-gray-400 font-semibold">Beaucoup</span>
        </div>
      </div>

      {groups.map((group) => (
        <section key={group.phase}>
          <p className="text-[10px] uppercase tracking-widest text-gray-400 font-semibold mb-2">
            {PHASE_LABELS[group.phase as BreadcrumbPhase] || group.phase}
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 lg:grid-cols-6 gap-2">
            {group.rows.map((row) => (
              <HeatTile key={row.id} row={row} />
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}
